/**
 * Life4Earth V2 — React hook for project partners.
 *
 * Returns active partners for a given project, keyed by project GUID
 * in the query cache. Subscribes to cache invalidation events so
 * SignalR broadcasts trigger automatic re-render.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchProjectPartners,
  createProjectPartner,
  updateProjectPartner,
  deleteProjectPartner,
} from "./projectPartners";
import type {
  ProjectPartnerResponse,
  CreateProjectPartnerRequest,
  UpdateProjectPartnerRequest,
} from "./projectPartners";
import {
  fetchForKey,
  invalidateAndRefetch,
  CACHE_UPDATE_EVENT,
  QUERY_KEYS,
} from "./queryCache";

// ── Hook result type ────────────────────────────────────────────

export interface UseApiProjectPartnersResult {
  partners: ProjectPartnerResponse[];
  loading: boolean;
  error: string | null;
  source: "api" | "seed" | "loading";
  refetch: () => void;
  addPartner: (body: CreateProjectPartnerRequest) => Promise<ProjectPartnerResponse>;
  editPartner: (partnerId: string, body: UpdateProjectPartnerRequest) => Promise<void>;
  removePartner: (partnerId: string) => Promise<void>;
}

// ── Hook ────────────────────────────────────────────────────────

export function useApiProjectPartners(
  projectGuid: string | undefined
): UseApiProjectPartnersResult {
  const mountedRef = useRef(true);
  const cacheKey = projectGuid
    ? `${QUERY_KEYS.projectPartners}:${projectGuid}`
    : "";

  const [state, setState] = useState<
    Pick<UseApiProjectPartnersResult, "partners" | "loading" | "error" | "source">
  >({
    partners: [],
    loading: true,
    error: null,
    source: "loading",
  });

  const load = useCallback(
    async (force = false) => {
      if (!projectGuid || !mountedRef.current) return;
      try {
        const result = await fetchForKey(
          cacheKey,
          () => fetchProjectPartners(projectGuid),
          { force }
        );
        if (mountedRef.current) {
          setState({
            partners: result,
            loading: false,
            error: null,
            source: "api",
          });
        }
      } catch (err: any) {
        if (mountedRef.current) {
          setState((prev) => ({
            ...prev,
            loading: false,
            error: err.message,
            source: "seed",
          }));
        }
      }
    },
    [projectGuid, cacheKey]
  );

  // Initial load
  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => {
      mountedRef.current = false;
    };
  }, [load]);

  // Subscribe to cache invalidation
  useEffect(() => {
    function onCacheUpdate(e: Event) {
      const key = (e as CustomEvent).detail?.key;
      if (key === QUERY_KEYS.projectPartners || key === cacheKey) {
        load(true);
      }
    }
    window.addEventListener(CACHE_UPDATE_EVENT, onCacheUpdate);
    return () => window.removeEventListener(CACHE_UPDATE_EVENT, onCacheUpdate);
  }, [cacheKey, load]);

  // ── Mutations ─────────────────────────────────────────────────

  const addPartner = useCallback(
    async (body: CreateProjectPartnerRequest) => {
      if (!projectGuid) throw new Error("No project GUID");
      const created = await createProjectPartner(projectGuid, body);
      invalidateAndRefetch(cacheKey);
      return created;
    },
    [projectGuid, cacheKey]
  );

  const editPartner = useCallback(
    async (partnerId: string, body: UpdateProjectPartnerRequest) => {
      if (!projectGuid) throw new Error("No project GUID");
      await updateProjectPartner(projectGuid, partnerId, body);
      invalidateAndRefetch(cacheKey);
    },
    [projectGuid, cacheKey]
  );

  const removePartner = useCallback(
    async (partnerId: string) => {
      if (!projectGuid) throw new Error("No project GUID");
      await deleteProjectPartner(projectGuid, partnerId);
      invalidateAndRefetch(cacheKey);
    },
    [projectGuid, cacheKey]
  );

  return {
    ...state,
    refetch: () => load(true),
    addPartner,
    editPartner,
    removePartner,
  };
}
